import { gql, type TypedDocumentNode } from '@apollo/client';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconHourglass } from '@sorare/core/src/atoms/icons/system/iconHourglass.ts';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyS } from '@sorare/core/src/atoms/typography';
import { Bold } from '@sorare/core/src/atoms/typography/Bold/index.tsx';
import { S27Chip } from '@sorare/core/src/atoms/ui/Chip/S27Chip/index.tsx';
import {
  S27Title,
  S27TitleDisplay,
} from '@sorare/core/src/atoms/ui/S27Title/index.tsx';
import { Dialog } from '@sorare/core/src/components/dialog';
import { CloseButton } from '@sorare/core/src/components/dialog/CloseButton';
import { useConfigContext } from '@sorare/core/src/contexts/config';
import { useTimeLeft } from '@sorare/core/src/hooks/useTimeLeft.tsx';
import {
  ensureHexColor,
  getRgbColorFromHex,
} from '@sorare/core/src/lib/colors.ts';
import { isType } from '@sorare/core/src/lib/gql';
import { css } from '@sorare/css';

import { AnyTaskBasicClaimStory } from '#src/components/missions/AnyTaskBasicClaimStory/index.tsx';
import { isClaimableNow } from '#src/components/series/isClaimableNow.ts';

import { RewardSlot, type RewardSlotState } from './RewardSlot/index.tsx';
import type {
  DailyRewardsCalendarBoard_cardSet,
  DailyRewardsCalendarBoard_taskInterface,
} from './__generated__/index.graphql.ts';

const DAY_MS = 24 * 60 * 60 * 1000;

const slotState = (step: {
  aasmState: ManagerProgressionTaskState;
  claimableAt: Date | null;
}): RewardSlotState => {
  if (step.aasmState === ManagerProgressionTaskState.CLAIMED) {
    return 'claimed';
  }
  return isClaimableNow(step) ? 'toClaim' : 'future';
};

const NextRewardCountdown = ({
  time,
  onEnded,
}: {
  time: Date;
  onEnded: () => void;
}) => {
  const { isEnded, message } = useTimeLeft(time);

  useEffect(() => {
    if (isEnded) {
      onEnded();
    }
  }, [isEnded, onEnded]);

  if (isEnded) {
    return null;
  }

  return (
    <BodyS color="var(--c-nd-600)">
      <FormattedMessage
        defaultMessage="Next reward in {timeLeft}"
        values={{ timeLeft: message }}
      />
    </BodyS>
  );
};

const daysUntil = (date: Date | null) => {
  if (!date) {
    return null;
  }

  const days = Math.ceil((new Date(date).getTime() - Date.now()) / DAY_MS);

  return days > 0 ? days : null;
};

const content = css`
  position: relative;
  isolation: isolate;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 100%;
  padding: calc(8 * var(--unit)) var(--double-unit) var(--double-unit);
  background:
    radial-gradient(
      120% 60% at 50% 0%,
      var(--board-glow, transparent) 0%,
      transparent 70%
    ),
    linear-gradient(
      180deg,
      transparent 0%,
      rgba(var(--c-rgb-black), 0.6) 45%,
      var(--c-black) 85%
    ),
    var(--board-bg, none) top center / cover no-repeat,
    var(--c-black);
`;
const close = css`
  position: absolute;
  top: var(--double-unit);
  right: var(--double-unit);
  z-index: 1;
`;
const body = css`
  flex: 1;
`;
const grid = css`
  flex: 1;
  width: 100%;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: 1fr 1fr 1.3fr;
  column-gap: var(--unit);
  row-gap: var(--double-unit);
`;

type Props = {
  calendarTasksTrack: DailyRewardsCalendarBoard_taskInterface;
  cardSet?: Nullable<DailyRewardsCalendarBoard_cardSet>;
  open: boolean;
  onClose: () => void;
  refetch: () => Promise<unknown>;
};

export const DailyRewardsCalendarBoard = ({
  calendarTasksTrack,
  cardSet,
  open,
  onClose,
  refetch,
}: Props) => {
  const {
    so5: { dailyTasksExpiresAt },
  } = useConfigContext();
  const [claimingStepId, setClaimingStepId] = useState<string | undefined>();

  if (!isType(calendarTasksTrack, 'TasksTrack')) {
    return null;
  }

  const { steps, currentStep, endDate } = calendarTasksTrack;
  const brandingColor = cardSet?.branding?.colors?.main;
  const boardGlow = brandingColor
    ? `rgba(${getRgbColorFromHex(ensureHexColor(brandingColor))}, 0.35)`
    : undefined;
  const backgroundUrl = cardSet?.branding?.assetUrl ?? undefined;

  const claimingStep = steps.find(step => step.id === claimingStepId);
  const hasClaimableReward = steps.some(isClaimableNow);
  const nextRewardAt = currentStep?.claimableAt ?? dailyTasksExpiresAt;
  const daysLeft = daysUntil(endDate);

  const lastIndex = steps.length - 1;

  const footer =
    nextRewardAt && !hasClaimableReward ? (
      <Vertical center gap={0}>
        <NextRewardCountdown time={nextRewardAt} onEnded={refetch} />
      </Vertical>
    ) : null;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        hideHeader
        maxWidth="xs"
        footer={footer}
      >
        <div
          className={content}
          style={
            {
              ...(boardGlow && { '--board-glow': boardGlow }),
              ...(backgroundUrl && { '--board-bg': `url("${backgroundUrl}")` }),
            } as React.CSSProperties
          }
        >
          <div className={close}>
            <CloseButton onClose={onClose} />
          </div>
          <Vertical gap={4} className={body}>
            <Vertical center gap={1}>
              {daysLeft !== null && (
                <S27Chip color="teal" size="small">
                  <FontAwesomeIcon icon={iconHourglass} />
                  <FormattedMessage
                    defaultMessage="Ends in {daysLeft}d"
                    values={{ daysLeft }}
                  />
                </S27Chip>
              )}
              <S27Title>
                <FormattedMessage
                  defaultMessage="Daily <display>rewards</display>"
                  values={{
                    display: chunks => (
                      <S27TitleDisplay>{chunks}</S27TitleDisplay>
                    ),
                  }}
                />
              </S27Title>
              <BodyS>
                <FormattedMessage
                  defaultMessage="<b>Free claim</b> every day for the first {count} days."
                  values={{ count: steps.length, b: Bold }}
                />
              </BodyS>
            </Vertical>

            <div className={grid}>
              {steps.map((step, index) => {
                const rewardConfig = step.rewardConfigs.at(0);
                if (!rewardConfig) {
                  return null;
                }
                return (
                  <RewardSlot
                    key={step.id}
                    rewardConfig={rewardConfig}
                    state={slotState(step)}
                    dayNumber={index + 1}
                    hero={index === lastIndex && steps.length > 1}
                    onClaim={() => setClaimingStepId(step.id)}
                  />
                );
              })}
            </div>
          </Vertical>
        </div>
      </Dialog>
      {!!claimingStep && (
        <AnyTaskBasicClaimStory
          task={claimingStep}
          onClose={() => {
            refetch();
            setClaimingStepId(undefined);
            onClose();
          }}
        />
      )}
    </>
  );
};

DailyRewardsCalendarBoard.fragments = {
  cardSet: gql`
    fragment DailyRewardsCalendarBoard_cardSet on CardSet {
      slug
      branding {
        id
        colors {
          main
        }
        assetUrl(slot: PLAY_DAILY_REWARDS_CALENDAR_BACKGROUND)
      }
    }
  ` as TypedDocumentNode<DailyRewardsCalendarBoard_cardSet>,
  taskInterface: gql`
    fragment DailyRewardsCalendarBoard_taskInterface on TaskInterface {
      id
      ... on TasksTrack {
        id
        endDate
        steps {
          id
          aasmState
          claimableAt
          rewardConfigs {
            id
            ...RewardSlot_anyRewardConfigInterface
          }
          ...AnyTaskBasicClaimStory_taskInterface
        }
        currentStep {
          id
          claimableAt
        }
      }
    }
    ${RewardSlot.fragments.anyRewardConfigInterface}
    ${AnyTaskBasicClaimStory.fragments.taskInterface}
  ` as TypedDocumentNode<DailyRewardsCalendarBoard_taskInterface>,
};
